// The surface-material skin hook (decision 6a8d3df0) — the contract that lets
// a pack dress the CHASSIS without ever being able to make itself illegible.
//
// The six guardrails, enforced by construction here rather than by review:
//   1. Chassis only — materials render behind .vw-chassis, never behind data.
//   2. The display face is not skinnable — nothing here touches --surface-*.
//   3. Material never carries meaning — status colours are not inputs.
//   4. Contrast floor — every generator is near-black-on-black texture.
//   5. Matte is the default finish — sheen is a separate, bounded dial
//      (--chassis-sheen), pointer-tracked in the Chassis component.
//   6. Procedural and DPR-rendered — a pack names a generator; it never
//      supplies pixels, so nothing goes soft on retina and nothing loads.
export interface MaterialTile {
  /** data: URL of one tileable tile. */
  url: string;
  /** CSS background-size, e.g. "18px 18px". */
  size: string;
}

export type MaterialGenerator = () => MaterialTile;

function dpr(): number {
  return Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, 3);
}

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  const scale = dpr();
  c.width = w * scale;
  c.height = h * scale;
  const x = c.getContext('2d');
  if (!x) throw new Error('2d canvas unavailable — material generation needs a browser');
  x.scale(scale, scale);
  return [c, x];
}

/** Rexx the product's matte carbon — 2×2 twill, 9px pitch is the blessed zone
 *  (26px reads as aftermarket vinyl, 4px degrades to grey noise). */
export function carbon(pitch = 9): MaterialTile {
  const S = pitch * 2;
  const [c, x] = makeCanvas(S, S);
  x.fillStyle = '#0a0b0d';
  x.fillRect(0, 0, S, S);
  const tow = (px: number, py: number, hz: boolean) => {
    const g = hz
      ? x.createLinearGradient(px, py, px, py + pitch)
      : x.createLinearGradient(px, py, px + pitch, py);
    g.addColorStop(0, '#101317');
    g.addColorStop(0.3, '#262b31');
    g.addColorStop(0.58, '#181b1f');
    g.addColorStop(1, '#0b0d0f');
    x.fillStyle = g;
    x.fillRect(px, py, pitch, pitch);
    const n = Math.max(5, Math.round(pitch / 1.15));
    for (let i = 1; i < n; i++) {
      const f = i / n;
      const t = py + pitch * f;
      const u = px + pitch * f;
      x.strokeStyle = `rgba(255,255,255,${(0.012 + 0.055 * Math.sin(f * Math.PI)).toFixed(3)})`;
      x.lineWidth = 0.45;
      x.beginPath();
      if (hz) {
        x.moveTo(px, t);
        x.lineTo(px + pitch, t);
      } else {
        x.moveTo(u, py);
        x.lineTo(u, py + pitch);
      }
      x.stroke();
      x.strokeStyle = 'rgba(0,0,0,.20)';
      x.beginPath();
      if (hz) {
        x.moveTo(px, t + 0.5);
        x.lineTo(px + pitch, t + 0.5);
      } else {
        x.moveTo(u + 0.5, py);
        x.lineTo(u + 0.5, py + pitch);
      }
      x.stroke();
    }
    x.fillStyle = 'rgba(0,0,0,.42)';
    if (hz) x.fillRect(px, py + pitch - 1, pitch, 1);
    else x.fillRect(px + pitch - 1, py, 1, pitch);
  };
  tow(0, 0, true);
  tow(pitch, 0, false);
  tow(0, pitch, false);
  tow(pitch, pitch, true);
  return { url: c.toDataURL(), size: `${S}px ${S}px` };
}

/** Workshop oak — the proof that the slot is a contract, not carbon plumbing:
 *  a completely different material, zero changes to any component. */
export function oak(width = 46): MaterialTile {
  const H = width * 3;
  const [c, x] = makeCanvas(width, H);
  const g = x.createLinearGradient(0, 0, width, 0);
  g.addColorStop(0, '#3b2517');
  g.addColorStop(0.4, '#4a2f1c');
  g.addColorStop(0.75, '#3f2818');
  g.addColorStop(1, '#33200f');
  x.fillStyle = g;
  x.fillRect(0, 0, width, H);
  // Long vertical fibres with slow lateral drift — matte, low contrast.
  for (let i = 0; i < width * 2.2; i++) {
    const px = Math.random() * width;
    const amp = 0.5 + Math.random() * 1.6;
    const light = Math.random() > 0.62;
    x.strokeStyle = light
      ? `rgba(190,150,110,${(0.03 + Math.random() * 0.07).toFixed(3)})`
      : `rgba(0,0,0,${(0.04 + Math.random() * 0.1).toFixed(3)})`;
    x.lineWidth = 0.4 + Math.random() * 0.7;
    x.beginPath();
    x.moveTo(px, 0);
    for (let y = 0; y <= H; y += 4) x.lineTo(px + Math.sin((y / H) * Math.PI * 2 + px) * amp, y);
    x.stroke();
  }
  // Occasional cathedral figure.
  for (let k = 0; k < 2; k++) {
    const cxp = Math.random() * width;
    x.strokeStyle = 'rgba(0,0,0,.07)';
    x.lineWidth = 1.1;
    x.beginPath();
    for (let y2 = 0; y2 <= H; y2 += 3) x.lineTo(cxp + Math.sin((y2 / H) * Math.PI) * (width * 0.28), y2);
    x.stroke();
  }
  return { url: c.toDataURL(), size: `${width}px ${H}px` };
}

/** CallToWrap's FILM — 35mm emulsion grain, at carbon's restraint.
 *
 * The blessed phrase for this pack's material is "film at carbon's restraint",
 * and the second half is the operative one. Film has an obvious costume —
 * sprocket holes, frame lines, a strip down the edge of the screen — and every
 * bit of it is a picture of film rather than a material. Guardrail 3 rules it
 * out anyway (material never carries meaning) and guardrail 4 would kill the
 * contrast. What is left is the thing film actually IS at the surface: silver
 * halide grain, near-black on black, no pattern the eye can name.
 *
 * SO THIS IS GRAIN AND NOTHING ELSE. No sprockets, no frame lines, no gate
 * weave. A parent looking at the rail should not think "film"; they should
 * think the chassis has a surface, which is what every material here is for.
 *
 * DETERMINISTIC, unlike oak's Math.random(). A material is regenerated on
 * every load and on every DPR change, and grain that reshuffles each time
 * makes the chassis shimmer between renders — subtle, and exactly the kind of
 * thing that reads as the app being unstable without anyone being able to say
 * why. The PRNG below is a plain LCG: same tile every time, on every machine.
 *
 * The tile is LARGER than carbon's or oak's (96px vs 9/46) because grain has
 * no structure to hide a seam behind — a small tile of noise repeats as a
 * visible plaid, which is the one way random dots can look designed.
 */
export function film(size = 96): MaterialTile {
  const [c, x] = makeCanvas(size, size);
  x.fillStyle = '#0a0b0d';
  x.fillRect(0, 0, size, size);

  // A plain LCG (Numerical Recipes constants). Seeded per call, so the tile is
  // byte-identical every time this runs.
  let seed = 0x9e3779b1;
  const rnd = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };

  // Two passes: the coarse clumps the eye reads as grain, then a fine dust
  // that keeps it from looking like dots. Both stay inside the tile — a dot
  // clipped at the edge is the seam this large tile exists to avoid.
  const grains = Math.round(size * size * 0.16);
  for (let i = 0; i < grains; i++) {
    const r = 0.35 + rnd() * 0.75;
    const px = r + rnd() * (size - r * 2);
    const py = r + rnd() * (size - r * 2);
    // Slightly more dark than light: emulsion grain reads as density, and a
    // 50/50 mix flattens into grey noise (the same failure carbon's own note
    // records at a 4px pitch).
    const light = rnd() > 0.58;
    x.fillStyle = light
      ? `rgba(214,222,230,${(0.014 + rnd() * 0.03).toFixed(3)})`
      : `rgba(0,0,0,${(0.05 + rnd() * 0.14).toFixed(3)})`;
    x.beginPath();
    x.arc(px, py, r, 0, Math.PI * 2);
    x.fill();
  }
  const dust = Math.round(size * size * 0.1);
  for (let i = 0; i < dust; i++) {
    const px = rnd() * size;
    const py = rnd() * size;
    x.fillStyle = `rgba(255,255,255,${(0.006 + rnd() * 0.012).toFixed(3)})`;
    x.fillRect(px, py, 0.5, 0.5);
  }
  return { url: c.toDataURL(), size: `${size}px ${size}px` };
}

/** The enumerated material registry — a pack's skin names one of THESE keys
 *  (via --mat-<name> in its token block); it cannot point at arbitrary pixels. */
export const MATERIALS: Record<string, MaterialGenerator> = {
  carbon: () => carbon(),
  oak: () => oak(),
  film: () => film(),
};

/** Render every registered material once and publish it as --mat-<name> /
 *  --mat-<name>-size on the root element. Call once at app start (and again on
 *  a devicePixelRatio change if you care about window moves across displays). */
export function registerMaterials(root: HTMLElement = document.documentElement): void {
  for (const [name, generate] of Object.entries(MATERIALS)) {
    const { url, size } = generate();
    root.style.setProperty(`--mat-${name}`, `url(${url})`);
    root.style.setProperty(`--mat-${name}-size`, size);
  }
}
